import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { AccountService } from '../../../account/services';
import { AccountFacade } from '../../../account/store/facade';
import { DidsWithLabel, SessionDTO } from '../../../account/store/states-models';
import { CallHistoryRecord, CallHistoryRecordType, DatabaseService, PresenceModel, AddressbookRecord, ADDRESSBOOK_TYPES, AddressbookService, PaginatedAPIAddressbookContacts, APIAddressbookFilter, APIAddressbookSearchProperties, PRESENCE_STATUS } from '../../../core/services';
import { NotificationService } from '../../../core/services/notifications';
import { StartCallOutputType } from './start-call-modal.model';
import { PresenceService } from '../../../core/services/sip/presence.service';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { RecordFullnamePipe } from '../../pipes';
import { UtilsService } from '../../services';
import { UserFacade } from '../../stores/user/facade/user.facade';
import { User } from '../../models/user.model';
import { DidPreview, IPbxUser, PbxDevice } from 'voxloud-types/dist/models';
import { DEBOUNCE_TIME_MS } from '../../constants/contacts.constants';
import { ContactSearchValue } from '../../../contacts/models/contacts.model';
import { EMPTY_CONTACT_RESULT } from '../../../contacts/constants/contacts.constants';

export interface StartCallContactModel {
  fullname: string;
  number: string;
  originalContact: any;
}

const SEARCH_PROPERTIES: APIAddressbookSearchProperties[] = [
  APIAddressbookSearchProperties.FIRST_NAME,
  APIAddressbookSearchProperties.LAST_NAME,
  APIAddressbookSearchProperties.PHONE_NUMBER
]


@Component({
  selector: 'start-call-modal',
  templateUrl: './start-call-modal.component.html',
  styleUrls: [
    '../../styles/contact-list/contact-list.scss',
    './start-call-modal.component.scss'
  ]
})
export class StartCallModalComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];

  public ADDRESSBOOK_TYPES = ADDRESSBOOK_TYPES;
  public PRESENCE_STATUS = PRESENCE_STATUS;

  public isNewCall: boolean = true;

  public currentOutboundNumber: DidsWithLabel = {
    label: '',
    number: ''
  };
  public outboundDidList: DidsWithLabel[];

  public currentDigitedValue: string = '';
  public currentCountryPhoneCode: string = '';
  public dialerValue: string = '';

  get isValidNumber(): boolean {
    const value = this.currentTab === 'dialer' ? this.dialerValue : this.currentDigitedValue;
    return this.utilsService.isValidPhoneNumber(value);
  }
  public searchResults: StartCallContactModel[] = [];
  public recents: CallHistoryRecord[] = [];

  private contacts: any[] = [];

  public outboundNumberLoading: boolean = false;
  public outboundLoadFailed: boolean = false;
  public initLoading: boolean = false;
  public searchLoading: boolean = false;
  public searchInput: FormControl<ContactSearchValue | null> = new FormControl<ContactSearchValue | null>(null);

  private presenceList: PresenceModel[];

  private session: SessionDTO;
  private filters: APIAddressbookFilter;

  public usersToSpy: User[] = [];
  public currentUserID: number;
  public currentTab: 'contacts' | 'dialer' = 'contacts';

  constructor(
    private modalRef: NgbActiveModal,
    private accountFacade: AccountFacade,
    private accountService: AccountService,
    private dbService: DatabaseService,
    private notificationService: NotificationService,
    private presenceService: PresenceService,
    private addressbookService: AddressbookService,
    private fullNamePipe: RecordFullnamePipe,
    private utilsService: UtilsService,
    private userFacade: UserFacade
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.accountFacade.session$.subscribe((session: SessionDTO) => {
        this.session = session;
        this.userFacade.getAllUsersWhoCanBeSpied(session.pbx_user_id).pipe(take(1)).subscribe(r => {
          this.usersToSpy = r;
        });
        this.loadOutboundDids();
      })
    );

    this.presenceService.presenceSubscriptions$.pipe(take(1)).subscribe(p => this.presenceList = p);
    this.subscriptions.push(
      this.presenceService.presenceSubscription$.subscribe((presence: PresenceModel) => {
        const contact = this.contacts.find(c => c.presenceId && c.presenceId === presence.entity);
        if(contact) contact.status = presence.status;
        const result = this.searchResults.find(c => c?.originalContact?.presenceId && c?.originalContact?.presenceId === presence.entity);
        if(result) result.originalContact.status = presence.status;
        let recent = this.recents.find(r => r.displayName.number === presence.entity);
        if(recent) recent.status = presence.status;
      })
    );

    this.initLoading = true;
    forkJoin(
      {
        locals: this.dbService.getAllAddressbookRecords(),
        recents: this.dbService.getSomeCallHistoryRecordsByDateWithTypes(5, [CallHistoryRecordType.OUTGOING_ANSWERED, CallHistoryRecordType.OUTGOING_MISSED], undefined, false, true)
      }
    ).subscribe((res: {
      locals: AddressbookRecord[],
      recents: CallHistoryRecord[]
    }) => {
      this.contacts = res.locals;
      this.recents = res.recents;
      this.subscribeInternalUsersToPresence(this.contacts, this.recents);
      if(this.currentDigitedValue) this.onInputChange().subscribe();
      this.initLoading = false;
    }, () => {
      this.initLoading = false;
      this.notificationService.error('common.error-fetching-contacts');
    });

    this.subscriptions.push(
      this.searchInput.valueChanges.pipe(
        debounceTime(DEBOUNCE_TIME_MS),
        distinctUntilChanged((prev, curr) => {
          if (!prev && !curr) return true;
          if (!prev || !curr) return false;

          return prev.input === curr.input &&
                 JSON.stringify(prev.searchProperties) === JSON.stringify(curr.searchProperties);
        }),
        tap((value) => {
          this.searchLoading = !!value?.input;
        }),
        switchMap(() => {
          if (this.searchInput.value && this.searchInput.value.input) {
            this.currentDigitedValue = this.searchInput.value.input;
            this.currentCountryPhoneCode = this.searchInput.value.countryPhoneCode || '';
            return this.onInputChange();
          } else {
            this.currentDigitedValue = '';
            this.currentCountryPhoneCode = '';
            this.searchLoading = false;
            this.searchResults = [];
            return of(void 0);
          }
        })
      ).subscribe()
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public close(): void {
    this.modalRef.close();
  }

  private loadOutboundDids(): void {
    this.outboundNumberLoading = true;
    this.outboundLoadFailed = false;
    const getUser = this.accountService.getUser(this.session.pbx_id, this.session.pbx_user_id);
    const getDevice = this.accountService.getDevices(this.session.pbx_id, this.session.pbx_user_id).pipe(
      map((devices: PbxDevice[]) => devices.find((device: PbxDevice) => device.id === this.session.device_id))
    );
    forkJoin({ user: getUser, device: getDevice }).subscribe({
      next: (response: { user: IPbxUser, device: PbxDevice | undefined }) => {
        this.currentUserID = response.user.id;
        this.outboundDidList = response.user.outbound_config.available_dids.map((did: DidPreview) => ({
          label: did.label,
          number: did.number,
          id: did.id,
          isWhatsappEnabled: did.is_whatsapp_enabled
        }));
        if (!response.device) {
          this.outboundLoadFailed = true;
          this.outboundNumberLoading = false;
          return;
        }
        this.currentOutboundNumber = {
          label: response.device.outbound_config.outbound_did.label,
          number: response.device.outbound_config.outbound_did.number,
          id: response.device.outbound_config.outbound_did.id,
          isWhatsappEnabled: response.device.outbound_config.outbound_did.is_whatsapp_enabled
        };
        this.outboundNumberLoading = false;
      },
      error: () => {
        this.outboundLoadFailed = true;
        this.outboundNumberLoading = false;
      }
    });
  }

  public retryOutboundLoad(): void {
    this.loadOutboundDids();
  }

  public onSelectOutboundDid(did: DidsWithLabel): void {
    this.outboundNumberLoading = true;
    this.accountService.updateOutboundDid(
      this.session.pbx_id, 
      this.session.pbx_user_id, 
      this.session.device_id, 
      did.number
    ).subscribe((response: DidPreview) => {
      this.currentOutboundNumber = did;
      this.accountFacade.setOutboundNumber(did.number);
      this.outboundNumberLoading = false;
    },() => {
      this.notificationService.error('start-call-modal.errors.update-outbound-failed');
      this.outboundNumberLoading = false;
    })
  }

  public onInputChange(): Observable<void> {
    this.filters = {
        input: this.currentDigitedValue.toLowerCase(),
        properties: this.searchInput.value?.searchProperties || SEARCH_PROPERTIES
      }
      return this.addressbookService.getContacts(this.session.company_id, this.filters).pipe(catchError(() => {
        return this.fetchContactsErrorHandler();
      }), map((externals) => {
        let tmpRes = [...this.contacts.filter(this.filterContacts, this), ...externals.content.map(this.addressbookService.fromAPIAddressbookRecord)];
        this.searchResults = tmpRes.map(this.mapContacts, this).slice(0, 5);
        this.searchLoading = false;
      }));
  }


  private filterContacts(contact: AddressbookRecord): boolean {
    const searchTerm = this.currentDigitedValue.toLowerCase().trim();
    return `${contact.firstName.toLowerCase()} ${contact.lastName?.toLowerCase()}`.includes(searchTerm) || contact.defaultNumber?.number?.includes(searchTerm);
  }

  private mapContacts(contact: any): StartCallContactModel {
    return {
      fullname: this.fullNamePipe.transform(contact),
      number: contact.defaultNumber?.number,
      originalContact: contact
    }
  }

  public onDialerKeyPressed(event: string): void {
    this.dialerValue += event;
  }

  public onBackspace(): void {
    if(this.dialerValue !== '') {
      this.dialerValue = this.dialerValue.slice(0, -1);
    }
  }

  get dialerFontModifier(): string | null {
    const len = this.dialerValue.length;
    if (len > 14) return 'start-call-modal__dialer-number--sm';
    if (len > 10) return 'start-call-modal__dialer-number--md';
    return null;
  }

  public callFromRecents(recent: CallHistoryRecord): void {
    this.modalRef.close({
      contact: recent,
      type: StartCallOutputType.EVENT
    })
  }
  public callFromContacts(contact: AddressbookRecord): void {
    let type: StartCallOutputType;
    switch(contact.type) {
      case ADDRESSBOOK_TYPES.EXTERNAL: {
        type = StartCallOutputType.EXTERNAL_VCARD;
      }
      break;
      case ADDRESSBOOK_TYPES.INTERNAL_USER: {
        type = StartCallOutputType.INTERNAL_USER
      }
      break;
      case ADDRESSBOOK_TYPES.INTERNAL_GROUP: {
        type = StartCallOutputType.INTERNAL_GROUP
      }
    }
    this.modalRef.close({
      contact: contact,
      type: type
    })
  }
  public callFromNumber(number: string): void {
    const cleanNumber = this.utilsService.cleanNumber(number);
    if(this.isValidNumber) {
      this.modalRef.close({
        contact: null,
        type: StartCallOutputType.NUMBER,
        number: cleanNumber
      })
    }
  }

  public callContactWithEnter(): void {
    if (this.searchResults.length !== 0 && this.searchResults[0].originalContact) {
      this.callFromContacts(this.searchResults[0].originalContact);
    } else if (this.isValidNumber) {
      this.callFromNumber(this.currentDigitedValue);
    }
  }

  private subscribeInternalUsersToPresence(users: AddressbookRecord[], recents: CallHistoryRecord[]) {
    users.forEach(u => {
      if(u.type === ADDRESSBOOK_TYPES.INTERNAL_USER){
        const idx = this.presenceList.findIndex(value => value.entity === u.presenceId);
        if(idx === -1) {
          this.presenceService.subscribeEntity(u.presenceId || u.defaultNumber.number);
        } else {
          u.status = this.presenceList[idx].status;
        }
      }
    });
    recents.forEach((recent) => {
      if(recent.addressbookType === ADDRESSBOOK_TYPES.INTERNAL_USER){
        const idx = this.presenceList.findIndex(v => v.entity === recent.displayName.number);
        if(idx === -1){
          this.presenceService.subscribeEntity(recent.displayName.number);
        } else {
          recent.status = this.presenceList[idx].status;
        }
      }
    })
  }

  private fetchContactsErrorHandler(): Observable<PaginatedAPIAddressbookContacts> {
    this.notificationService.error('common.error-fetching-contacts');
    this.searchLoading = false;
    return of(EMPTY_CONTACT_RESULT);
  }

  public switchTab(t: 'contacts' | 'dialer') {
    this.currentTab = t;
  }
}
